import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, signal } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { ServiceVisit, ServiceVisitDrawerData } from "../../models/service-visit.model";
import { DialogManagerService } from "@shared/services/dialog-manager.service";
import { ImagesCarouselComponent } from "@shared/components/ui/images-carousel/images-carousel.component";
import { ServiceVisitService } from "../../services/service-visit.service";
import { TenantUrlUtilService } from "@shared/services/tenant-url-util.service";
import { HorusMessageGeneralComponent } from "@shared/components/domain/horus-message-general/horus-message-general.component";

@Component({
  selector: "app-service-visits-drawer",
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    HorusMessageGeneralComponent,
  ],
  templateUrl: "./service-visits-drawer.component.html",
  styleUrl: "./service-visits-drawer.component.scss",
})
export class ServiceVisitsDrawerComponent implements OnInit {
  private readonly dialogManagerService = inject(DialogManagerService);
  private readonly serviceVisitService = inject(ServiceVisitService);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);

  readonly drawerData: ServiceVisitDrawerData = inject(MAT_DIALOG_DATA);

  readonly visits = signal<ServiceVisit[]>([]);
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);

  ngOnInit(): void {
    this.loadVisits();
  }

  async loadVisits(): Promise<void> {
    const tenantId = this.drawerData.tenantId || this.tenantUrlUtil.normalizedTenant();
    const serviceDetailId = this.drawerData.serviceDetailId;

    if (!tenantId || !serviceDetailId) {
      this.error.set("Faltan datos para cargar las visitas");
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);

    try {
      const visits = await this.serviceVisitService.getServiceVisits({
        tenantId,
        serviceDetailId,
      });
      this.visits.set(visits);
    } catch {
      this.error.set("Error al cargar las visitas");
    } finally {
      this.isLoading.set(false);
    }
  }

  onClose(): void {
    if (this.drawerData.close) {
      this.drawerData.close();
    }
  }

  onViewImages(images: ServiceVisit['images'], event: MouseEvent): void {
    event.stopPropagation();
    const imageUrls = images.map(img => img.imageUrl);
    this.dialogManagerService.openCarousel(ImagesCarouselComponent, imageUrls, event);
  }

  getStatusLabel(visit: ServiceVisit): string {
    return visit.statusDescriptionHomologate
      || visit.statusDescription
      || (visit.status === 'NO_REALIZADA' ? 'No entregado' : visit.status);
  }

  getStatusClass(visit: ServiceVisit): string {
    const statusUpper = visit.status.toUpperCase();
    const statusLabelUpper = this.getStatusLabel(visit).toUpperCase();

    if (statusUpper === 'COMPLETADO' || statusUpper === 'COMPLETED' || statusLabelUpper === 'REALIZADO') {
      return 'status-completed';
    }
    if (statusUpper === 'NO_REALIZADA' || statusUpper === 'CANCELLED' || statusUpper === 'CANCELED' || statusLabelUpper === 'NO ENTREGADO') {
      return 'status-not-done';
    }
    return 'status-pending';
  }

  /**
   * El backend normaliza las fechas de los proveedores a ISO-8601 sin zona
   * (yyyy-MM-ddTHH:mm:ss), que el parser nativo interpreta en hora local.
   * Devuelve null ante un valor vacío o no parseable para que la vista muestre
   * un guion en lugar de una fecha inventada.
   */
  formatDate(dateStr: string): Date | null {
    if (!dateStr) return null;

    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
}
